import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, X, Users, Archive, Headphones, ShoppingCart, Smartphone, LogOut, MessageSquarePlus, MessageSquare, Tag, ChevronDown, Settings, MailWarning, BarChart3, ArrowLeft } from 'lucide-react';
import { Avatar } from '../../components/Avatar';
import { Interruptor } from '../../components/Interruptor';
import { useAuth } from '../../lib/auth';
import { useSocket } from '../../lib/socket';
import { useToast } from '../../components/Toast';
import { useAlertModal } from '../../components/DialogProvider';
import { atendimentoApi, recusaComOutraAtendente } from './lib/api';
import { useEtiquetas } from './lib/etiquetas-context';
import { BadgeEtiqueta } from './componentes/BadgeEtiqueta';
import { ListaConversas } from './componentes/ListaConversas';
import { PainelChat } from './componentes/PainelChat';
import { ModalSalvarCliente } from './modais/ModalSalvarCliente';
import { ModalEtiquetaContato } from './modais/ModalEtiquetaContato';
import { ModalPerfilContato } from './modais/ModalPerfilContato';
import { ModalNovaConversa } from './modais/ModalNovaConversa';
import { ModalMensagensNaoEntregues } from './modais/ModalMensagensNaoEntregues';
import { ModalAppMetricas } from './modais/ModalAppMetricas';
import { tocarBeep, tocarAlertaChamado, destravarAutoplay } from './lib/beep';
import type { Contato, Conversa, Inbox, Etiqueta } from './lib/tipos';

type Filtro = 'todas' | 'nao_lidas' | 'grupos' | 'arquivadas' | 'sem_atribuicao' | 'em_aberto' | 'etiqueta';


const DEPT_LABEL: Record<Inbox, string> = { atendimento: 'Atendimento', compras: 'Compras', app: 'App' };
const DEPT_ICON: Record<Inbox, typeof Headphones> = { atendimento: Headphones, compras: ShoppingCart, app: Smartphone };

export function AtendimentoHome() {
  const { usuario, logout } = useAuth();
  const { socket, connected } = useSocket();
  // Filtro do topo mostra so' as visiveis — filtrar por nome de vendedora nunca foi usado
  // (conferido com ele em 08/09/2026) e era o unico custo de ocultar as de agente.
  const { visiveis: etiquetasTodas, atualizar: atualizarEtiquetas } = useEtiquetas();
  const toast = useToast();
  const { alerta } = useAlertModal();
  // Falha ao ABRIR conversa: "outra atendente esta com o contato" vai em popup (pedido do Ruan,
  // 28/09/2026 — ela precisa ler com quem o cliente esta); o resto segue no toast.
  function avisarFalhaAoAbrir(e: any, padrao: string) {
    const outra = recusaComOutraAtendente(e);
    if (outra) alerta(outra, { titulo: 'Contato em atendimento', tipo: 'aviso' });
    else toast.erro(e?.message ?? padrao);
  }

  // Espelha as flags que vieram do /me assim que o usuario carrega.
  useEffect(() => {
    if (usuario?.assinatura_ativa !== undefined) setAssinaturaAtiva(usuario.assinatura_ativa);
  }, [usuario?.assinatura_ativa]);
  useEffect(() => {
    if (usuario?.recebe_chamados !== undefined) setNoRodizio(usuario.recebe_chamados);
  }, [usuario?.recebe_chamados]);

  // O interruptor do rodizio so faz sentido pra quem participa do rodizio. Admin nao entra no
  // round-robin (o agent-selection filtra `perfil <> 'admin'`), e quem nao tem o departamento
  // Atendimento tambem nao — pra eles o interruptor nao teria efeito nenhum e ainda deixaria a
  // engrenagem amarela pra sempre. Ler `usuario.departamentos` CRU de proposito: a constante
  // `departamentos` abaixo devolve as duas caixas pra admin/gerente (e' pra montar as abas),
  // entao ela mentiria aqui.
  const participaDoRodizio =
    usuario?.perfil !== 'admin' && (usuario?.departamentos ?? []).includes('atendimento');

  const abrirConfig = useCallback(() => {
    if (btnConfigRef.current) {
      const rect = btnConfigRef.current.getBoundingClientRect();
      // Abre PRA CIMA (o botao vive no rodape) e alinhado pela esquerda,
      // sem deixar vazar pela direita da janela.
      const LARGURA = 260;
      setConfigPos({
        left: Math.max(8, Math.min(rect.left, window.innerWidth - LARGURA - 8)),
        bottom: window.innerHeight - rect.top + 6,
      });
    }
    setMostrarConfig(true);
  }, []);

  const alternarAssinatura = useCallback(async (valor: boolean) => {
    setAssinaturaAtiva(valor);
    setMostrarConfig(false);
    try {
      await atendimentoApi.definirAssinaturaAtiva(valor);
    } catch {
      setAssinaturaAtiva(!valor);
      toast.erro('Nao foi possivel salvar a configuracao');
    }
  }, [toast]);

  // Entrar/sair do rodizio de chamados. Desligar devolve pra fila os chamados que a pessoa
  // nunca respondeu — e ela precisa VER isso acontecer, senao parece que o cliente sumiu.
  const alternarRodizio = useCallback(async (valor: boolean) => {
    setNoRodizio(valor);
    setMostrarConfig(false);
    try {
      const r = await atendimentoApi.definirRodizio(valor);
      if (!valor && r.devolvidos > 0) {
        toast.sucesso(`${r.devolvidos} chamado(s) sem resposta ${r.devolvidos > 1 ? 'voltaram' : 'voltou'} pra fila`);
      }
    } catch {
      setNoRodizio(!valor);
      toast.erro('Nao foi possivel salvar a configuracao');
    }
  }, [toast]);

  // admin/gerente sempre veem todas as caixas; vendedor ve so seus departamentos.
  //
  // ⚠️ A caixa `app` (chat do aplicativo do cliente) e' DERIVADA de `atendimento`,
  // nao um departamento novo no cadastro do usuario. Quem atende o app sao as
  // MESMAS vendedoras — decisao do Ruan, e o rodizio ja trata as duas como uma so
  // (RDS chamados 1.27). Uma caixinha nova na ficha de cada usuario seria mais um
  // lugar pra alguem esquecer de marcar, e o sintoma seria mudo: a vendedora
  // receberia o chamado do app e nunca veria a aba onde ele aparece.
  const ehAdmin = usuario?.perfil === 'admin' || usuario?.perfil === 'gerente';
  const departamentosBase: Inbox[] = ehAdmin
    ? ['atendimento', 'compras']
    : (((usuario?.departamentos as Inbox[] | undefined) ?? ['atendimento']) as Inbox[]);
  const departamentos: Inbox[] = departamentosBase.includes('atendimento')
    ? [...departamentosBase, 'app']
    : departamentosBase;
  const temMultiplasDepts = departamentos.length > 1;

  const [inboxAtiva, setInboxAtiva] = useState<Inbox>(() => {
    const salvo = localStorage.getItem('fiodecobre_inbox') as Inbox | null;
    return salvo && departamentos.includes(salvo) ? salvo : (departamentos[0] ?? 'atendimento');
  });
  // Configuracoes da minha conta (gear do rodape). Painel via portal: o rodape fica
  // no fim de uma coluna com overflow, entao dropdown ancorado ali seria cortado.
  const [mostrarConfig, setMostrarConfig] = useState(false);
  const [painelEntrega, setPainelEntrega] = useState(false);
  // Uso do AppCel (Onda App Metricas) — icone admin-only ao lado da aba "App".
  const [metricasAppAberta, setMetricasAppAberta] = useState(false);
  const [configPos, setConfigPos] = useState({ left: 0, bottom: 0 });
  // `null` = ainda NAO SEI (o /me nao respondeu, ou o backend nao mandou o campo). Comecar em
  // `true` chutava: o interruptor afirmava "ligado" sem saber, e foi assim que o rodizio apareceu
  // ligado numa maquina recem-logada com o banco dizendo desligado (06/08, achado pelo Ruan — a
  // causa era o /login mandar menos campos que o /me, ja corrigida no backend). Enquanto nao sei,
  // nao desenho o interruptor: sumir e um defeito que alguem relata, mostrar errado nao.
  const [assinaturaAtiva, setAssinaturaAtiva] = useState<boolean | null>(null);
  const [noRodizio, setNoRodizio] = useState<boolean | null>(null);
  const btnConfigRef = useRef<HTMLButtonElement>(null);
  // Declarado DEPOIS dos estados de proposito: ler `assinaturaAtiva`/`noRodizio` antes do
  // `useState` que os cria estoura em ReferenceError (zona morta do const).
  // Comparacao explicita com `false` — `!null` seria true e pintaria a engrenagem de amarelo
  // durante o carregamento, avisando de um problema que ninguem tem.
  const contaForaDoPadrao = assinaturaAtiva === false || (participaDoRodizio && noRodizio === false);

  const [conversas, setConversas] = useState<Conversa[]>([]);
  const [conversaAtiva, setConversaAtiva] = useState<Conversa | null>(null);
  const [busca, setBusca] = useState('');
  const [buscaModo, setBuscaModo] = useState<'contato' | 'mensagens'>('contato');
  const [contatosSemConversa, setContatosSemConversa] = useState<Contato[]>([]);
  const [abrindoContato, setAbrindoContato] = useState(false);
  const [filtro, setFiltro] = useState<Filtro>('todas');
  // Filtro por etiqueta (igual ChatFC antigo): seleciona uma etiqueta e a lista mostra so
  // as conversas cujo contato tem essa etiqueta. Selecao unica, excludente com os demais
  // filtros (escolher etiqueta vira filtro='etiqueta'; escolher outro filtro o desativa).
  const [etiquetaFiltroId, setEtiquetaFiltroId] = useState<number | null>(null);
  const [mobileChat, setMobileChat] = useState(false);
  const [salvarCliente, setSalvarCliente] = useState<Conversa | null>(null);
  const [etiquetar, setEtiquetar] = useState<Conversa | null>(null);
  const [perfil, setPerfil] = useState<Conversa | null>(null);
  const [novaConversaAberta, setNovaConversaAberta] = useState(false);
  const restauradoRef = useRef(false);
  const ultPorInbox = useRef<Record<string, Conversa | null>>({});

  // ENTRADA POR LINK — o botao de WhatsApp da Gestao de Vendas cai aqui ([DA-672]):
  //   /atendimento?telefone=64999999999&nome=FULANO&voltar=/orcamentos/fila
  //
  // ⚠️ O `voltar` fica na URL, e nao no `state` do navigate: o caminho de volta tem que sobreviver
  // ao F5 e ao voltar do navegador, nao so a seta ([DA-670] p.3, mesma licao do `fila-retomada:ultimo`).
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const voltarPara = params.get('voltar');
  const deepLinkRef = useRef(false);
  // Dedup de alerta de chamado novo (conversa_id -> ultimo aviso ms) + flag pro titulo piscar.
  const chamadoAlertadoRef = useRef<Map<number, number>>(new Map());
  const [alertaChamado, setAlertaChamado] = useState(false);
  // Contador de nao-lidas por inbox pra bolinha nas abas (DA-164). A aba ATIVA usa o calculo
  // local (instantaneo, sempre certo); as outras vem deste fetch, atualizado ao vivo (debounced)
  // por qualquer evento de socket — o front so carrega as conversas da aba ativa.
  const [contadores, setContadores] = useState<Partial<Record<Inbox, number>>>({});
  // Contador "Em Aberto" por inbox (chamados atribuidos ainda nao encerrados) — bolinha
  // amarela no filtro. Mesmo ciclo de atualizacao dos contadores de nao-lidas.
  const [emAberto, setEmAberto] = useState<Partial<Record<Inbox, number>>>({});
  const contadoresTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const atualizarContadores = useCallback(() => {
    atendimentoApi.contadoresNaoLidas().then(setContadores).catch(() => {});
    atendimentoApi.contadorEmAberto().then(setEmAberto).catch(() => {});
  }, []);
  const agendarContadores = useCallback(() => {
    if (contadoresTimerRef.current) clearTimeout(contadoresTimerRef.current);
    contadoresTimerRef.current = setTimeout(atualizarContadores, 800);
  }, [atualizarContadores]);

  // Destrava autoplay do beep no primeiro click/tecla (Chrome/Safari bloqueiam audio sem interacao)
  useEffect(() => {
    function unlock() {
      destravarAutoplay();
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    }
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  // Zera padding/overflow do .app-content e .app-root pra atendimento ocupar 100% sem scroll fora
  useLayoutEffect(() => {
    const content = document.querySelector('.app-content') as HTMLElement | null;
    const root = document.querySelector('.app-root') as HTMLElement | null;
    if (!content) return;
    const padOrig = content.style.padding;
    const overflowOrig = content.style.overflow;
    const rootOverflowOrig = root?.style.overflow ?? '';
    content.style.padding = '0';
    content.style.overflow = 'hidden';
    if (root) root.style.overflow = 'hidden';
    return () => {
      content.style.padding = padOrig;
      content.style.overflow = overflowOrig;
      if (root) root.style.overflow = rootOverflowOrig;
    };
  }, []);

  const carregarConversas = useCallback(async () => {
    try {
      const params = {
        inbox: inboxAtiva,
        status: (filtro === 'sem_atribuicao' ? 'sem_atribuicao'
          : filtro === 'arquivadas' ? 'arquivadas'
          : 'todas') as 'todas' | 'sem_atribuicao' | 'arquivadas',
        busca: busca || undefined,
        busca_modo: busca ? buscaModo : undefined,
        mostrar_arquivadas: filtro === 'arquivadas',
        // A etiqueta vai pro SERVIDOR (pedido dele em 15/10/2026): filtrar aqui, sobre as 200
        // conversas mais recentes que a rota devolve, escondia quem tinha a etiqueta e conversa antiga.
        etiqueta_id: filtro === 'etiqueta' && etiquetaFiltroId != null ? etiquetaFiltroId : undefined,
        // Em Aberto / Nao lidas / Grupos tambem no SERVIDOR (15/10/2026): na tela, sobre as 200
        // mais recentes, 33 dos 54 chamados abertos ficavam de fora. A regra do "Em Aberto"
        // (atribuido, nao encerrado, com mensagem trocada, sem auto-conversa de equipe — DA-123,
        // RDS 1.16) mora na rota, igual a do contador da bolinha.
        filtro: filtro === 'em_aberto' || filtro === 'nao_lidas' || filtro === 'grupos' ? filtro : undefined,
      };
      const lista = await atendimentoApi.listarConversas(params);
      setConversas(lista);
    } catch {
      // silencia
    }
  }, [inboxAtiva, busca, buscaModo, filtro, etiquetaFiltroId]);

  useEffect(() => {
    carregarConversas();
    const t = setInterval(carregarConversas, 15000);
    return () => clearInterval(t);
  }, [carregarConversas]);

  // Busca debounced de contatos sem conversa na inbox ativa.
  // Mostra so quando ha busca >= 2 chars no modo 'contato'; desativa no modo 'mensagens'.
  // E so no filtro 'Tudo': com outro filtro marcado a lista mostra SO o que ele seleciona
  // (pedido do Ruan, 01/10/2026). Antes, com 'Arquivados' ligado, a agenda aparecia embaixo
  // e todo contato fora do filtro se lia como arquivado — inclusive quem tem conversa,
  // porque o "sem conversa" e medido contra a lista FILTRADA.
  useEffect(() => {
    const q = busca.trim();
    if (q.length < 2 || buscaModo === 'mensagens' || filtro !== 'todas') {
      setContatosSemConversa([]);
      return;
    }
    let cancelado = false;
    const t = setTimeout(() => {
      atendimentoApi.listarContatos({ busca: q, inbox: inboxAtiva, sem_grupo: true, limit: 30 })
        .then((lst) => {
          if (cancelado) return;
          const jidsComConversa = new Set(conversas.map((c) => c.contato.jid));
          setContatosSemConversa(lst.filter((c) => !jidsComConversa.has(c.jid)).slice(0, 20));
        })
        .catch(() => { if (!cancelado) setContatosSemConversa([]); });
    }, 250);
    return () => { cancelado = true; clearTimeout(t); };
  }, [busca, buscaModo, inboxAtiva, conversas, filtro]);

  useEffect(() => {
    atendimentoApi.listarEtiquetas().then(atualizarEtiquetas).catch(() => {});
  }, [atualizarEtiquetas]);

  // Contadores das abas: busca no mount e re-busca (debounced) a cada evento de socket,
  // de qualquer inbox — assim a bolinha da aba que NAO esta aberta tambem fica viva.
  useEffect(() => { atualizarContadores(); }, [atualizarContadores]);
  useEffect(() => {
    if (!socket) return;
    const bump = () => agendarContadores();
    socket.on('new_message', bump);
    socket.on('conversation_updated', bump);
    return () => { socket.off('new_message', bump); socket.off('conversation_updated', bump); };
  }, [socket, agendarContadores]);

  useEffect(() => {
    if (restauradoRef.current || !conversas.length) return;
    restauradoRef.current = true;
    const id = localStorage.getItem('fiodecobre_conversa_ativa');
    if (id) {
      const c = conversas.find((x) => x.id === Number(id));
      if (c) setConversaAtiva(c);
    }
  }, [conversas]);

  // Abre a conversa que veio no LINK ([DA-672]).
  //
  // ⚠️ **Nao procura na lista carregada — usa o `POST /api/conversas`, que e' idempotente.** A
  // lista e' a da inbox ativa, filtrada e paginada: procurar nela faria o botao funcionar quase
  // sempre, e "quase sempre" num botao de acao e' beco sem saida (a vendedora clica, nao acontece
  // nada e nao ha o que ela possa fazer). O mesmo cano ja e' usado pelo "Outros contatos" e pelo
  // "Conversar" do cartao de contato — numero que ja tem conversa so e' TRAZIDO, nao recriado.
  useEffect(() => {
    if (deepLinkRef.current) return;
    const telefone = params.get('telefone');
    if (!telefone) return;
    deepLinkRef.current = true;
    // O link manda mais que a ultima conversa aberta: sem isto, a restauracao acima abriria outra
    // conversa por cima desta assim que a lista chegasse.
    restauradoRef.current = true;

    const inboxAlvo = ((params.get('inbox') as Inbox) || 'atendimento');
    const nome = params.get('nome') || undefined;
    if (inboxAlvo !== inboxAtiva) {
      setInboxAtiva(inboxAlvo);
      localStorage.setItem('fiodecobre_inbox', inboxAlvo);
    }
    void (async () => {
      try {
        const conv = await atendimentoApi.novaConversa({ telefone, inbox: inboxAlvo, nome });
        setConversas((prev) => (prev.some((x) => x.id === conv.id) ? prev : [conv, ...prev]));
        setConversaAtiva(conv);
        localStorage.setItem('fiodecobre_conversa_ativa', String(conv.id));
        setMobileChat(true);
      } catch (e: any) {
        // Falha aqui deixa a pessoa numa tela de Atendimento sem contexto nenhum. O aviso diz o
        // numero, que e' o que ela precisa pra achar na mao — toast mudo seria pior que erro.
        avisarFalhaAoAbrir(e, `Nao consegui abrir a conversa com ${telefone}`);
      }
    })();
  }, [params, inboxAtiva, toast]);

  // Socket events na lista
  useEffect(() => {
    if (!socket) return;
    const meuId = usuario?.id;
    // Chamado novo atribuido a MIM: som de alerta + toast + (se aba escondida) titulo piscando.
    function notificarNovoChamado(nome: string | null) {
      tocarAlertaChamado();
      toast.aviso(nome ? `Novo atendimento atribuido a voce: ${nome}` : 'Novo atendimento atribuido a voce');
      setAlertaChamado(true);
    }
    function aoConvUpdate(data: any) {
      if (data?.inbox && data.inbox !== inboxAtiva) return;
      const ehMeuChamado = meuId != null && Number(data.usuario_atribuido_id) === meuId;
      setConversas((prev) => {
        const idx = prev.findIndex((c) => c.id === Number(data.id));
        // Detecta a transicao "nao era meu -> agora e meu" (ou conversa nova pra mim).
        if (ehMeuChamado) {
          const eraMeu = idx >= 0 && Number((prev[idx] as any).usuario_atribuido_id) === meuId;
          const ultimo = chamadoAlertadoRef.current.get(Number(data.id)) ?? 0;
          if (!eraMeu && Date.now() - ultimo > 5000) {
            chamadoAlertadoRef.current.set(Number(data.id), Date.now());
            const nome = idx >= 0 ? (prev[idx]!.contato?.nome ?? null) : null;
            setTimeout(() => notificarNovoChamado(nome), 0); // fora do ciclo de render
          }
        }
        if (idx < 0) { carregarConversas(); return prev; }
        const updated = { ...prev[idx]!, ...data };
        const next = [...prev];
        next[idx] = updated;
        next.sort((a, b) => new Date(b.ultima_mensagem_em || 0).getTime() - new Date(a.ultima_mensagem_em || 0).getTime());
        return next;
      });
      if (conversaAtiva?.id === Number(data.id)) setConversaAtiva((p) => (p ? { ...p, ...data } : p));
    }
    function aoNova(msg: any) {
      const conversaId = Number(msg?.conversa_id ?? msg?.mensagem?.conversa_id);
      if (!conversaId) return;
      const m = msg.mensagem ?? msg;
      // Beep so quando inbound, mesma inbox ativa, conversa nao silenciada/arquivada.
      // Confia primeiro no payload do socket (backend envia inbox/silenciada/arquivada),
      // cai pra lookup no array local se a flag nao veio.
      if (m?.direcao === 'inbound') {
        const inboxMsg = msg.inbox ?? m.inbox;
        const inboxBate = !inboxMsg || inboxMsg === inboxAtiva;
        let muted = msg.silenciada === true || m.silenciada === true;
        let arquivada = msg.arquivada === true || m.arquivada === true;
        // Se nao veio no payload, tenta achar localmente
        if (msg.silenciada === undefined && m.silenciada === undefined) {
          const conv = conversas.find((c) => c.id === conversaId);
          muted = conv?.silenciada ?? false;
          arquivada = conv?.arquivada ?? false;
        }
        if (inboxBate && !muted && !arquivada) tocarBeep();
      }
      // se nao temos a conversa, recarrega
      setConversas((prev) => {
        const idx = prev.findIndex((c) => c.id === conversaId);
        if (idx < 0) { carregarConversas(); return prev; }
        const preview = m.tipo === 'image' ? '[Imagem]' : m.tipo === 'audio' ? '[Audio]' : (m.texto ?? m.midia_nome ?? '[Midia]');
        const updated: Conversa = {
          ...prev[idx]!,
          ultima_mensagem_em: m.criado_em,
          ultima_mensagem_preview: String(preview).slice(0, 200),
          ultima_mensagem_status: m.status ?? 'sent',
          ultima_mensagem_direcao: m.direcao,
          nao_lidas: conversaId === conversaAtiva?.id ? 0 : prev[idx]!.nao_lidas + (m.direcao === 'inbound' ? 1 : 0),
        };
        const next = [...prev];
        next[idx] = updated;
        next.sort((a, b) => new Date(b.ultima_mensagem_em || 0).getTime() - new Date(a.ultima_mensagem_em || 0).getTime());
        return next;
      });
    }
    socket.on('conversation_updated', aoConvUpdate);
    socket.on('new_message', aoNova);
    return () => {
      socket.off('conversation_updated', aoConvUpdate);
      socket.off('new_message', aoNova);
    };
  }, [socket, inboxAtiva, conversaAtiva, carregarConversas, usuario?.id, toast]);

  // Reload na volta ao foco + limpa o alerta de chamado novo (atendente ja esta olhando).
  useEffect(() => {
    const onVis = () => { if (!document.hidden) { carregarConversas(); atualizarContadores(); setAlertaChamado(false); } };
    const onFocus = () => setAlertaChamado(false);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
  }, [carregarConversas, atualizarContadores]);

  // Reconnect catch-up
  const prevConn = useRef(connected);
  useEffect(() => {
    if (connected && !prevConn.current) carregarConversas();
    prevConn.current = connected;
  }, [connected, carregarConversas]);

  // Page title com nao-lidas; pisca "NOVO ATENDIMENTO" enquanto houver chamado novo
  // pendente (alertaChamado limpa quando a aba ganha foco).
  useEffect(() => {
    const total = conversas.reduce((s, c) => s + (c.arquivada ? 0 : c.nao_lidas), 0);
    const base = total > 0 ? `(${total}) Atendimento - FIODECOBRE` : 'Atendimento - FIODECOBRE';
    if (!alertaChamado) { document.title = base; return; }
    let on = true;
    document.title = '🔔 NOVO ATENDIMENTO';
    const t = setInterval(() => { on = !on; document.title = on ? '🔔 NOVO ATENDIMENTO' : base; }, 1000);
    return () => clearInterval(t);
  }, [conversas, alertaChamado]);

  function selecionar(c: Conversa) {
    setConversaAtiva(c);
    localStorage.setItem('fiodecobre_conversa_ativa', String(c.id));
    setMobileChat(true);
    setConversas((prev) => prev.map((x) => (x.id === c.id ? { ...x, nao_lidas: 0 } : x)));
  }

  function trocarInbox(novo: Inbox) {
    if (conversaAtiva) ultPorInbox.current[inboxAtiva] = conversaAtiva;
    setInboxAtiva(novo);
    localStorage.setItem('fiodecobre_inbox', novo);
    setConversaAtiva(ultPorInbox.current[novo] ?? null);
  }

  async function iniciarConversaComContato(c: Contato) {
    if (abrindoContato) return;
    setAbrindoContato(true);
    try {
      const conv = await atendimentoApi.novaConversa({ jid: c.jid, inbox: inboxAtiva, nome: c.nome });
      setConversas((prev) => (prev.some((x) => x.id === conv.id) ? prev : [conv, ...prev]));
      setContatosSemConversa([]);
      selecionar(conv);
    } catch (e: any) {
      avisarFalhaAoAbrir(e, 'Falha ao abrir conversa');
    } finally {
      setAbrindoContato(false);
    }
  }

  // "Conversar" do cartao de contato (vCard) recebido/enviado no chat: abre a conversa
  // com AQUELE numero na inbox atual. Mesmo cano do "Outros contatos" da lista lateral —
  // `POST /api/conversas` e idempotente, entao numero que ja tem conversa so a traz.
  async function conversarComNumero(telefone: string, nome: string) {
    if (abrindoContato) return;
    setAbrindoContato(true);
    try {
      const conv = await atendimentoApi.novaConversa({ telefone, inbox: inboxAtiva, nome: nome || undefined });
      setConversas((prev) => (prev.some((x) => x.id === conv.id) ? prev : [conv, ...prev]));
      selecionar(conv);
    } catch (e: any) {
      avisarFalhaAoAbrir(e, 'Falha ao abrir conversa');
    } finally {
      setAbrindoContato(false);
    }
  }

  function aoAtualizarConversa(patch: Partial<Conversa> & { id: number }) {
    setConversas((prev) => {
      if ((patch.arquivada === true && filtro !== 'arquivadas')
        || (patch.arquivada === false && filtro === 'arquivadas')) {
        return prev.filter((c) => c.id !== patch.id);
      }
      // Encerrou estando no filtro "Em Aberto": sai da lista na hora (deixou de ser ativo).
      // Em "Tudo" e nas demais visoes, permanece (vira selo "Encerrado").
      if (patch.status === 'encerrado' && filtro === 'em_aberto') {
        return prev.filter((c) => c.id !== patch.id);
      }
      return prev.map((c) => (c.id === patch.id ? ({ ...c, ...patch } as Conversa) : c));
    });
    setConversaAtiva((p) => (p?.id === patch.id ? ({ ...p, ...patch } as Conversa) : p));
  }

  return (
    <div className="flex flex-col" style={{ height: '100%', minHeight: 0 }}>
      {/* VOLTAR PRA ONDE VEIO ([DA-672]) — so aparece quando se chegou aqui por link.
          ⚠️ Fica FORA dos dois paineis de proposito. Dentro da barra lateral ela sumiria no
          tablet assim que o chat abrisse (`hidden md:flex`), que e' exatamente o instante em que
          a vendedora vai querer voltar — a armadilha da [DA-655]: escape que depende de onde a
          tela esta nao e' escape.
          Preenchido, nao `btn-secundario`: botao unico de acao num cabecalho, sem par pra
          contrastar, e' lido como titulo ([DA-667]). */}
      {voltarPara && (
        <div className="flex-shrink-0 px-3 py-2" style={{ background: 'var(--wa-sidebar-bg, #1e1e1e)', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
          {/* ⚠️ `voltar=fechar` — quem chegou aqui em ABA NOVA (o botao de WhatsApp da campanha de
              fly) nao volta NAVEGANDO: navegar abriria uma SEGUNDA copia da tela de origem, e o
              que ele montou ficou vivo na aba anterior. Foi o relato dele em 11/09/2026: o voltar
              o largava na aba "Contatos", com a campanha em outra janela.
              O fallback existe porque fechar por script pode ser recusado pelo navegador; nesse
              caso ele ao menos chega na tela de origem, que e' o comportamento antigo. */}
          <button
            type="button"
            onClick={() => {
              if (voltarPara === 'fechar') {
                window.close();
                setTimeout(() => { if (!window.closed) navigate('/orcamentos/fila'); }, 200);
                return;
              }
              navigate(voltarPara);
            }}
            className="flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-1.5"
            style={{ backgroundColor: '#2563eb', color: '#ffffff', border: 'none' }}
          >
            <ArrowLeft size={15} />
            Voltar para {params.get('rotulo') || 'a tela anterior'}
          </button>
        </div>
      )}
      <div className="flex overflow-hidden" style={{ flex: 1, minHeight: 0 }}>
      <div className={`w-full md:w-[432px] border-r flex flex-col flex-shrink-0 ${mobileChat ? 'hidden md:flex' : 'flex'}`} style={{ background: 'var(--wa-sidebar-bg, #1e1e1e)' }}>
        {temMultiplasDepts && (
          <div className="flex flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
            {departamentos.map((dept) => {
              const Icon = DEPT_ICON[dept] ?? Headphones;
              const ativo = inboxAtiva === dept;
              // Aba ativa: total local (instantaneo). Outras: contador do servidor (ao vivo via socket).
              const naoLidas = ativo
                ? conversas.reduce((s, c) => s + (c.arquivada ? 0 : c.nao_lidas), 0)
                : (contadores[dept] ?? 0);
              return (
                <button
                  key={dept}
                  onClick={() => trocarInbox(dept)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[17px] font-medium transition border-b-2 ${ativo ? '' : 'border-transparent hover:bg-white/5'}`}
                  style={ativo ? {
                    color: 'var(--wa-text-fg, #e5e7eb)',
                    borderBottomColor: 'var(--wa-text-fg, #e5e7eb)',
                    background: 'color-mix(in srgb, var(--wa-text-fg, #e5e7eb) 12%, transparent)',
                  } : { color: 'var(--wa-subtext-fg, #9ca3af)' }}
                >
                  <Icon size={14} /> {DEPT_LABEL[dept] ?? dept}
                  {naoLidas > 0 && (
                    <span style={{
                      background: '#fde68a', color: '#000', fontSize: 16, fontWeight: 700,
                      minWidth: 24, height: 24, borderRadius: 12, padding: '0 7px',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      lineHeight: 1, fontVariantNumeric: 'tabular-nums',
                    }}>
                      {naoLidas > 99 ? '99+' : naoLidas}
                    </span>
                  )}
                </button>
              );
            })}
            {/* Uso do AppCel — relatorio de quem esta usando o aplicativo do cliente.
                So admin ve (mesma regra do Diario do Usuario, DA-411); a rota do
                backend recusa de novo, que e quem protege de verdade. */}
            {ehAdmin && (
              <button
                type="button"
                onClick={() => setMetricasAppAberta(true)}
                title="Uso do AppCel — quem esta usando"
                className="flex-shrink-0 flex items-center justify-center hover:bg-white/5"
                style={{ width: 36, color: 'var(--wa-subtext-fg, #9ca3af)' }}
              >
                <BarChart3 size={15} />
              </button>
            )}
          </div>
        )}

        <div className="flex items-center justify-center gap-1 px-1.5 py-1.5 flex-shrink-0 flex-wrap" style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
          <BotaoFiltro ativo={filtro === 'todas'} onClick={() => setFiltro('todas')}>Tudo</BotaoFiltro>
          {/* "Em Aberto" = chamados atribuidos ainda nao encerrados. Vale nas duas caixas
              (na Compras o Ruan usa pra triar fornecedores — Onda Encerramento de Chamado). */}
          <BotaoFiltro ativo={filtro === 'em_aberto'} onClick={() => setFiltro(filtro === 'em_aberto' ? 'todas' : 'em_aberto')}>
            Em Aberto
            {(emAberto[inboxAtiva] ?? 0) > 0 && (
              <span style={{
                background: '#fde68a', color: '#000', fontSize: 11, fontWeight: 700,
                minWidth: 18, height: 18, borderRadius: 9, padding: '0 5px',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                lineHeight: 1, fontVariantNumeric: 'tabular-nums',
              }}>
                {(emAberto[inboxAtiva] ?? 0) > 99 ? '99+' : emAberto[inboxAtiva]}
              </span>
            )}
          </BotaoFiltro>
          <BotaoFiltro ativo={filtro === 'nao_lidas'} onClick={() => setFiltro(filtro === 'nao_lidas' ? 'todas' : 'nao_lidas')}>Nao lidas</BotaoFiltro>
          {/* "Sem agente" so faz sentido pra admin/gerente — a vendedora so ve as dela. */}
          {ehAdmin && (
            <BotaoFiltro ativo={filtro === 'sem_atribuicao'} onClick={() => setFiltro(filtro === 'sem_atribuicao' ? 'todas' : 'sem_atribuicao')}>Sem agente</BotaoFiltro>
          )}
          <BotaoFiltro ativo={filtro === 'grupos'} onClick={() => setFiltro(filtro === 'grupos' ? 'todas' : 'grupos')} cor="verde">
            <Users size={12} /> Grupos
          </BotaoFiltro>
          <BotaoFiltro ativo={filtro === 'arquivadas'} onClick={() => setFiltro(filtro === 'arquivadas' ? 'todas' : 'arquivadas')} cor="cinza">
            <Archive size={12} /> Arquivados
          </BotaoFiltro>
          <FiltroEtiquetas
            etiquetas={etiquetasTodas}
            selecionadaId={etiquetaFiltroId}
            ativo={filtro === 'etiqueta'}
            onSelecionar={(id) => { setEtiquetaFiltroId(id); setFiltro('etiqueta'); }}
            onLimpar={() => { setFiltro('todas'); setEtiquetaFiltroId(null); }}
          />
        </div>

        <div className="px-3 pt-2 pb-0 flex-shrink-0 flex items-center gap-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }} />
            <input
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder={buscaModo === 'mensagens' ? 'Buscar nas mensagens...' : 'Buscar contato ou cliente...'}
              className="w-full pl-8 pr-8 py-1.5 text-sm rounded-lg outline-none"
              style={{
                color: 'var(--wa-text-fg, #e5e7eb)',
                background: 'color-mix(in srgb, var(--wa-text-fg, #ffffff) 10%, transparent)',
              }}
            />
            {busca && (
              <button onClick={() => setBusca('')} className="absolute right-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}>
                <X size={14} />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setBuscaModo((m) => m === 'mensagens' ? 'contato' : 'mensagens')}
            title={buscaModo === 'mensagens' ? 'Buscando nas mensagens — clique para voltar ao contato' : 'Buscar nas mensagens'}
            className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition"
            style={{
              color: buscaModo === 'mensagens' ? 'var(--wa-chat-header-bg, #1a73e8)' : 'var(--wa-text-fg, #e5e7eb)',
              background: buscaModo === 'mensagens'
                ? 'color-mix(in srgb, var(--wa-text-fg, #ffffff) 25%, transparent)'
                : 'color-mix(in srgb, var(--wa-text-fg, #ffffff) 10%, transparent)',
            }}
          >
            <MessageSquare size={16} />
          </button>
          {/* Toda a equipe inicia conversa nova desde a [DA-765] (28/09/2026) — o servidor ja
              aceitava, so este botao seguia so-admin. O modal limita as caixas aos departamentos
              de quem abre, e a conversa em andamento com outra atendente segue dando popup. */}
            <button
              type="button"
              onClick={() => setNovaConversaAberta(true)}
              title="Nova conversa"
              className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition"
              style={{
                color: 'var(--wa-text-fg, #e5e7eb)',
                background: 'color-mix(in srgb, var(--wa-text-fg, #ffffff) 10%, transparent)',
              }}
            >
              <MessageSquarePlus size={16} />
            </button>
        </div>

        <ListaConversas
          conversas={conversas}
          ativaId={conversaAtiva?.id ?? null}
          onSelecionar={selecionar}
          onAtualizar={aoAtualizarConversa}
          onSalvarComoCliente={(c) => setSalvarCliente(c)}
          onEtiquetar={(c) => setEtiquetar(c)}
          onPerfil={(c) => setPerfil(c)}
          contatosSemConversa={contatosSemConversa}
          onIniciarConversaComContato={iniciarConversaComContato}
          abrindoContato={abrindoContato}
        />

        <div className="px-3 py-2 flex items-center justify-between flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.12)', background: 'color-mix(in srgb, var(--wa-sidebar-bg, #1e1e1e) 85%, #000 15%)' }}>
          <div className="flex items-center gap-2 min-w-0">
            <div className="relative">
              <Avatar nome={usuario?.nome ?? '?'} fotoUrl={usuario?.foto_url} tamanho={32} />
              <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 ${connected ? 'bg-green-500' : 'bg-gray-500'}`} style={{ borderColor: 'transparent' }} />
            </div>
            <div className="min-w-0">
              <span className="text-xs truncate block" style={{ color: 'var(--wa-text-fg, #e5e7eb)' }}>{usuario?.nome}</span>
              <span className="text-[10px]" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}>{connected ? 'Online' : 'Offline'}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Configuracoes da MINHA conta de atendimento (assinatura e, no futuro, o resto). */}
            <button
              ref={btnConfigRef}
              onClick={() => (mostrarConfig ? setMostrarConfig(false) : abrirConfig())}
              title="Configurações da minha conta"
              className="inline-flex items-center justify-center rounded-lg transition"
              style={{
                width: 28,
                height: 28,
                // Amarelo quando ALGUMA das chaves esta fora do padrao — assinatura desligada ou
                // fora do rodizio. Sao os estados que a pessoa precisa notar sem procurar: sair do
                // rodizio e esquecer e' silencioso (as colegas absorvem e nada quebra), entao o
                // lembrete tem que morar na tela dela o dia inteiro.
                background: contaForaDoPadrao ? '#fde68a' : 'rgba(255,255,255,0.12)',
                color: contaForaDoPadrao ? '#000' : 'var(--wa-text-fg, #e5e7eb)',
              }}
            >
              <Settings size={16} />
            </button>
            <button onClick={logout} className="p-1.5 hover:text-red-400 hover:bg-white/10 rounded-lg" title="Sair" style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}>
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className={`flex-1 flex ${mobileChat ? 'flex' : 'hidden md:flex'}`}>
        {conversaAtiva ? (
          <PainelChat
            conversa={conversaAtiva}
            onVoltar={() => { setMobileChat(false); setConversaAtiva(null); }}
            onAtualizarConversa={aoAtualizarConversa}
            onApagarContato={(contatoId) => {
              setConversas((prev) => prev.filter((c) => c.contato.id !== contatoId));
              setConversaAtiva(null);
              setMobileChat(false);
            }}
            onConversarComNumero={conversarComNumero}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center text-gray-400">
              <p className="text-lg">Selecione uma conversa</p>
              <p className="text-sm mt-1">Atendimento Fio de Cobre</p>
            </div>
          </div>
        )}
      </div>

      <ModalSalvarCliente
        aberto={!!salvarCliente}
        conversa={salvarCliente}
        onFechar={() => setSalvarCliente(null)}
        onAtualizado={(contato) => {
          if (!salvarCliente) return;
          aoAtualizarConversa({ id: salvarCliente.id, contato: { ...salvarCliente.contato, ...contato } });
          setSalvarCliente({ ...salvarCliente, contato: { ...salvarCliente.contato, ...contato } });
        }}
      />
      {/* Nome e etiqueta gravados sobem pra lista e pro cabecalho pelo mesmo caminho da tela do fly. */}
      <ModalEtiquetaContato
        conversa={etiquetar}
        onFechar={() => setEtiquetar(null)}
        onContatoAtualizado={(contato) => {
          if (!etiquetar) return;
          aoAtualizarConversa({ id: etiquetar.id, contato: { ...etiquetar.contato, ...contato } });
        }}
      />
      {/* Area sobe pelo caminho do Etiqueta; a Origem vem do GET /conversas, entao rele a lista. */}
      <ModalPerfilContato
        conversa={perfil}
        onFechar={() => setPerfil(null)}
        onContatoAtualizado={(contato) => {
          if (!perfil) return;
          aoAtualizarConversa({ id: perfil.id, contato: { ...perfil.contato, ...contato } });
        }}
        onOrigemGravada={() => void carregarConversas()}
      />
      <ModalNovaConversa
        aberto={novaConversaAberta}
        inboxInicial={inboxAtiva}
        onFechar={() => setNovaConversaAberta(false)}
        onAberta={(conv) => {
          setConversas((prev) => (prev.some((x) => x.id === conv.id) ? prev : [conv, ...prev]));
          if (conv.inbox !== inboxAtiva) setInboxAtiva(conv.inbox);
          selecionar(conv);
        }}
      />

      <ModalMensagensNaoEntregues aberto={painelEntrega} onFechar={() => setPainelEntrega(false)} />
      {ehAdmin && (
        <ModalAppMetricas aberto={metricasAppAberta} onFechar={() => setMetricasAppAberta(false)} />
      )}

      {/* Painel de configuracoes da minha conta — gear do rodape. Portal no body
          pra nao ser cortado pelo overflow da coluna da lista. */}
      {mostrarConfig && createPortal(
        <>
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 4000 }}
            onClick={() => setMostrarConfig(false)}
          />
          <div
            style={{
              position: 'fixed',
              left: configPos.left,
              bottom: configPos.bottom,
              zIndex: 4001,
              backgroundColor: 'var(--panel-bg, #fff)',
              border: '1px solid var(--border, #e5e7eb)',
              borderRadius: 8,
              padding: '12px 16px',
              minWidth: 260,
              boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
            }}
          >
            <p style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: 'var(--text-color)' }}>
              Minha conta
            </p>
            {assinaturaAtiva !== null && (
              <Interruptor
                ligado={assinaturaAtiva}
                onChange={alternarAssinatura}
                rotuloLigado="Assinatura ativa"
                rotuloDesligado="Assinatura desligada"
                ajuda={assinaturaAtiva
                  ? 'Suas mensagens saem com seu nome e a loja'
                  : 'Suas mensagens saem sem identificação'}
              />
            )}
            {/* So pra quem participa do rodizio — admin e quem nao atende nao entram no
                round-robin, e um interruptor sem efeito e pior que interruptor nenhum. */}
            {participaDoRodizio && noRodizio !== null && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--page-bg, #e5e7eb)' }}>
                <Interruptor
                  ligado={noRodizio}
                  onChange={alternarRodizio}
                  rotuloLigado="Recebendo chamados"
                  rotuloDesligado="Fora do rodízio"
                  ajuda={noRodizio
                    ? 'Novos clientes do WhatsApp podem cair pra você'
                    : 'Os chamados vão pras outras pessoas do balcão'}
                />
              </div>
            )}

            {/* Porta PERMANENTE do painel de entrega (DA-504). A faixa do topo so acende quando o
                canal foge do padrao; sem uma porta que existe sempre, a ferramenta desaparece
                junto com o alarme e ninguem lembra que ela existe. Secao propria porque nao e
                configuracao "da minha conta" — e ferramenta de conferencia. */}
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--page-bg, #e5e7eb)' }}>
              <button
                type="button"
                onClick={() => { setMostrarConfig(false); setPainelEntrega(true); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                  background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                  color: 'var(--text-color)', font: 'inherit', fontSize: 13,
                }}
              >
                <MailWarning size={16} style={{ flexShrink: 0, color: 'var(--primary)' }} />
                <span style={{ fontWeight: 600 }}>Mensagens não entregues</span>
              </button>
              <p style={{ fontSize: 11, opacity: 0.75, marginTop: 3, color: 'var(--text-color)' }}>
                O que saiu daqui e o WhatsApp ainda não confirmou
              </p>
            </div>
          </div>
        </>,
        document.body,
      )}
      </div>
    </div>
  );
}

// Filtro por etiqueta — botao "Etiquetas" que abre um menu com as etiquetas cadastradas
// (cada uma com seu badge colorido), igual ao ChatFC antigo. Selecao unica; quando ativo,
// o botao mostra a etiqueta escolhida + um "X" pra limpar. Menu em position:fixed ancorado
// no botao (getBoundingClientRect) pra nao ser cortado por overflow do container.
function FiltroEtiquetas({ etiquetas, selecionadaId, ativo, onSelecionar, onLimpar }: {
  etiquetas: Etiqueta[];
  selecionadaId: number | null;
  ativo: boolean;
  onSelecionar: (id: number) => void;
  onLimpar: () => void;
}) {
  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  function alternar() {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, left: r.left });
    setAberto((v) => !v);
  }

  const sel = etiquetas.find((e) => e.id === selecionadaId) ?? null;
  const className = `px-2 py-1 text-xs font-medium rounded-full border transition flex items-center gap-0.5 whitespace-nowrap`;

  return (
    <div className="flex items-center gap-0.5">
      <button
        ref={btnRef}
        onClick={alternar}
        className={ativo ? className : `${className} hover:bg-white/10`}
        style={ativo ? {
          background: 'var(--wa-text-fg, #ffffff)',
          color: 'var(--wa-sidebar-bg, #1f2937)',
          borderColor: 'var(--wa-text-fg, #ffffff)',
        } : {
          color: 'var(--wa-subtext-fg, #9ca3af)',
          borderColor: 'color-mix(in srgb, var(--wa-subtext-fg, #9ca3af) 50%, transparent)',
        }}
      >
        <Tag size={12} /> {ativo && sel ? (sel.apelido || sel.nome) : 'Etiquetas'}
        <ChevronDown size={12} />
      </button>
      {ativo && (
        <button
          onClick={onLimpar}
          title="Limpar filtro de etiqueta"
          className="p-0.5 rounded hover:bg-white/10 hover:text-red-400"
          style={{ color: 'var(--wa-subtext-fg, #9ca3af)' }}
        >
          <X size={14} />
        </button>
      )}
      {aberto && pos && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setAberto(false)} />
          <div
            className="fixed z-[70] rounded-lg shadow-lg border py-1 w-52 max-h-80 overflow-y-auto"
            style={{ top: pos.top, left: pos.left, background: 'var(--panel-bg, #fff)', borderColor: 'var(--panel-border, rgba(0,0,0,0.15))' }}
          >
            {etiquetas.length === 0 ? (
              <div className="px-3 py-2 text-sm" style={{ color: 'var(--text-muted, #94a3b8)' }}>Nenhuma etiqueta cadastrada</div>
            ) : (
              etiquetas.map((e) => (
                <button
                  key={e.id}
                  onClick={() => { onSelecionar(e.id); setAberto(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-black/5 ${selecionadaId === e.id ? 'font-semibold' : ''}`}
                  style={{ color: 'var(--text-color, #111)' }}
                >
                  <BadgeEtiqueta nome={e.nome} cor={e.cor} apelido={e.apelido} />
                  <span className="truncate">{e.nome}</span>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
}

function BotaoFiltro({ children, ativo, onClick, cor }: { children: React.ReactNode; ativo: boolean; onClick: () => void; cor?: 'verde' | 'cinza' }) {
  // Verde/cinza sao categorias semanticas (Grupos / Arquivados) — ficam fixos.
  // Default ativo inverte tema: bg = wa_text_fg, fg = wa_sidebar_bg → contraste sempre.
  const className = `px-2 py-1 text-xs font-medium rounded-full border transition flex items-center gap-0.5 whitespace-nowrap`;
  if (ativo && cor === 'verde') return <button onClick={onClick} className={`${className} bg-green-600 text-white border-green-600`}>{children}</button>;
  if (ativo && cor === 'cinza') return <button onClick={onClick} className={`${className} bg-gray-500 text-white border-gray-500`}>{children}</button>;
  if (ativo) return (
    <button
      onClick={onClick}
      className={className}
      style={{
        background: 'var(--wa-text-fg, #ffffff)',
        color: 'var(--wa-sidebar-bg, #1f2937)',
        borderColor: 'var(--wa-text-fg, #ffffff)',
      }}
    >
      {children}
    </button>
  );
  return (
    <button
      onClick={onClick}
      className={`${className} hover:bg-white/10`}
      style={{
        color: 'var(--wa-subtext-fg, #9ca3af)',
        borderColor: 'color-mix(in srgb, var(--wa-subtext-fg, #9ca3af) 50%, transparent)',
      }}
    >
      {children}
    </button>
  );
}
